import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSelectableTreatments, getValidActiveTreatmentId, resolveActiveTreatment } from 'src/utils/activeTreatment';

const ActiveTreatmentContext = createContext(null);

/**
 * Reflects an explicit ?treatmentId= deep-link for the lead-detail page, read-only.
 *
 * Multi-treatment rework: this provider intentionally does NOT inject a treatmentId
 * into the URL when one is missing, and does NOT auto-bridge into
 * currentTreatmentDataGlobal. Views default to "all treatments" / local tab filters;
 * currentTreatmentDataGlobal is set ONLY by actions (transient action-context). The
 * provider merely resolves ?treatmentId= when a deep-link supplies one.
 */
export function ActiveTreatmentProvider({ leadDetail, children }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedId = searchParams.get('treatmentId');

    const selectableTreatments = useMemo(() => getSelectableTreatments(leadDetail), [leadDetail]);
    const activeTreatmentId = useMemo(
        () => getValidActiveTreatmentId(leadDetail, requestedId),
        [leadDetail, requestedId]
    );
    const activeTreatment = useMemo(
        () => resolveActiveTreatment(leadDetail, activeTreatmentId),
        [leadDetail, activeTreatmentId]
    );

    // No URL injection and no auto-bridge to currentTreatmentDataGlobal (see the
    // provider note above). Deep-links are honoured by reading ?treatmentId= directly
    // where needed (e.g. the Treatments tab); actions set the treatment context.

    const setActiveTreatmentId = useCallback(
        (id) => {
            const next = new URLSearchParams(searchParams);
            next.set('treatmentId', String(id));
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const value = useMemo(
        () => ({ activeTreatmentId, setActiveTreatmentId, activeTreatment, selectableTreatments }),
        [activeTreatmentId, setActiveTreatmentId, activeTreatment, selectableTreatments]
    );

    return <ActiveTreatmentContext.Provider value={value}>{children}</ActiveTreatmentContext.Provider>;
}

ActiveTreatmentProvider.propTypes = {
    leadDetail: PropTypes.object,
    children: PropTypes.node,
};

export function useActiveTreatment() {
    const ctx = useContext(ActiveTreatmentContext);
    if (ctx === null) {
        // Safe inert default so consumers outside the provider don't crash.
        return { activeTreatmentId: null, setActiveTreatmentId: () => {}, activeTreatment: null, selectableTreatments: [] };
    }
    return ctx;
}
